import { useMediaQuery } from '@mnd-frontend/hooks';
import { breakpoints, media } from '@mnd-frontend/ui/publish/utils';
import { GREY_300, SHADOW_1, SPACING_2 } from '@mnd-frontend/ui/shared/tokens';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useMenuDataQuery } from '../../../../generated/hooks';
import mndUrl, { SubDomain } from '../../../../lib/mndUrl';
import routes from '../../../../routes';
import DesktopNavigation from './components/DesktopNavigation';
import MobileNavigation from './components/MobileNavigation';
import { type Item, MenuState } from './types';
import useMainMenu from './useMainMenu';

const Header = styled.header`
  position: fixed;
  top: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  padding: 0 ${SPACING_2};
  background-color: ${GREY_300};
  width: 100%;
  height: var(--header-height-mobile);
  box-shadow: ${SHADOW_1};

  ${media.greaterThan('navigationBreakpoint')} {
    position: unset;
    z-index: unset;
    background-color: unset;
    display: block;
    padding: 0;
    width: unset;
    box-shadow: unset;
  }
`;

interface NavigationProps {
  pressroomId?: string;
  sourceId?: string;
  isMynewsdeskAdmin?: boolean;
}

const Navigation = ({ pressroomId, sourceId, isMynewsdeskAdmin }: NavigationProps) => {
  const { t } = useTranslation();
  const [isSubNavOpen, setIsSubNavOpen] = useState<MenuState | null>(null);
  const variables = useMemo(
    () => ({
      pressroomId: pressroomId ?? '',
      skipPressroom: !pressroomId,
      sourceId: sourceId ?? '',
      skipSource: !sourceId,
    }),
    [pressroomId, sourceId],
  );

  const { data, loading } = useMenuDataQuery({
    variables,
  });

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.navigationBreakpoint - 1}px)`);
  const topGroup = [
    MenuState.Dashboard,
    MenuState.Publish,
    MenuState.Contacts,
    MenuState.Analyze,
    MenuState.Monitor,
    MenuState.Reports,
  ];
  const navigationItems = useMainMenu(data);
  const pressrooms = data?.source?.pressrooms ?? [];
  const siteCode = data?.pressroom?.siteCode;
  const renderPressroomSelector = !!(
    data?.source?.pressrooms &&
    data.source.pressrooms.length > 1 &&
    data?.pressroom
  );
  const trial = data?.source?.trial;
  const isOnlyJournalist = !!data?.me?.user?.isOnlyJournalist;
  const journalistItems: Item[] = [
    {
      url: mndUrl(SubDomain.WWW, `/${data?.pressroom?.siteCode ?? ''}/stories`),
      text: t('journalist.menu.stories'),
    },
    {
      url: pressroomId
        ? routes.userSettingsWithPressroomRoot({ pressroomId })
        : routes.userSettingsRoot(),
      text: t('menu_user_settings'),
    },
    {
      url: mndUrl(SubDomain.WWW, `/${data?.pressroom?.siteCode ?? ''}/stories/alerts`),
      text: t('journalist.menu.subscriptions'),
    },
    {
      url: mndUrl(SubDomain.Publish, '/user/signout'),
      text: t('menu_user_logout'),
    },
  ];
  const newsroomLogo =
    data?.pressroom?.newsroomSettings?.newsroomStyle?.favicon?.url ||
    data?.pressroom?.newsroomSettings?.newsroomStyle?.logotype?.url;

  return (
    <Header>
      {isMobile ? (
        <MobileNavigation
          pressroomName={data?.pressroom?.name ?? ''}
          navigationItems={navigationItems}
          isMynewsdeskAdmin={isMynewsdeskAdmin ?? false}
          renderPressroomSelector={renderPressroomSelector}
          trial={trial ?? false}
          siteCode={siteCode}
          isSubNavOpen={isSubNavOpen}
          pressrooms={pressrooms}
          isOnlyJournalist={isOnlyJournalist}
          journalistItems={journalistItems}
          topGroup={topGroup}
          setIsSubNavOpen={setIsSubNavOpen}
          newsroomLogo={newsroomLogo}
        />
      ) : (
        <DesktopNavigation
          pressroomName={data?.pressroom?.name ?? ''}
          loading={loading}
          navigationItems={navigationItems}
          renderPressroomSelector={renderPressroomSelector}
          isSubNavOpen={isSubNavOpen}
          trial={trial}
          topGroup={topGroup}
          pressrooms={pressrooms}
          isOnlyJournalist={isOnlyJournalist}
          siteCode={siteCode}
          journalistItems={journalistItems}
          setIsSubNavOpen={setIsSubNavOpen}
          newsroomLogo={newsroomLogo}
        />
      )}
    </Header>
  );
};

export default Navigation;
